// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import axios, { AxiosResponse } from 'axios';
import {
  ConsoleSession,
  SessionClaims,
  MFAClaims,
  AuthenticateRequest,
  AuthenticateMFASetupRequest,
  AuthenticateMFASetupResponse,
  UserRole
} from '../types/auth';

const SESSION_LOCALSTORAGE_KEY = 'currentSession';

class AuthenticationService {
  private session: ConsoleSession | null = null;
  private config = {
    host: process.env.REACT_APP_API_HOST || 'http://localhost:7351' // Default Nakama console port
  };

  constructor() {
    this.loadSessionFromStorage();
  }

  private loadSessionFromStorage(): void {
    const storedSession = localStorage.getItem(SESSION_LOCALSTORAGE_KEY);
    if (storedSession) {
      try {
        this.session = JSON.parse(storedSession);
      } catch (error) {
        console.error('Failed to parse stored session:', error);
        localStorage.removeItem(SESSION_LOCALSTORAGE_KEY);
      }
    }
  }

  public getSession(): ConsoleSession | null {
    return this.session;
  }

  public getUsername(): string {
    if (!this.session) return '';
    const claims = this.getClaims();
    return claims?.usn || '';
  }

  public getSessionRole(): UserRole {
    if (!this.session) return UserRole.USER_ROLE_UNKNOWN;
    
    const claims = this.getClaims();
    if (!claims) return UserRole.USER_ROLE_UNKNOWN;

    const role = claims.rol as number;
    switch (role) {
      case 1:
        return UserRole.USER_ROLE_ADMIN;
      case 2:
      case 3:
      case 4:
        return UserRole.USER_ROLE_MODIFIER;
      default:
        return UserRole.USER_ROLE_UNKNOWN;
    }
  }

  public getClaims(): SessionClaims | null {
    if (!this.session?.token) return null;
    return this.decodeJWT(this.session.token);
  }

  public getMFA(): MFAClaims | null {
    if (!this.session?.mfa_code) return null;
    return this.decodeJWT(this.session.mfa_code);
  }

  public isMfaRequired(): boolean {
    const mfa = this.getMFA();
    return mfa?.mfa_required || false;
  }

  public async login(username: string, password: string, code?: string): Promise<AxiosResponse<ConsoleSession>> {
    const req: AuthenticateRequest = {
      username,
      password,
      mfa: code || '',
    };

    try {
      const response = await axios.post<ConsoleSession>(
        `${this.config.host}/v2/console/authenticate`,
        req
      );

      this.session = response.data;
      localStorage.setItem(SESSION_LOCALSTORAGE_KEY, JSON.stringify(response.data));

      return response;
    } catch (error) {
      throw error;
    }
  }

  public async logout(): Promise<void> {
    if (!this.session) return;

    try {
      await axios.post(`${this.config.host}/v2/console/authenticate/logout`, {}, {
        headers: {
          Authorization: `Bearer ${this.session.token}`
        }
      });
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      localStorage.removeItem(SESSION_LOCALSTORAGE_KEY);
      this.session = null;
    }
  }

  public async mfaSetup(code: string): Promise<AuthenticateMFASetupResponse> {
    if (!this.session?.mfa_code) {
      throw new Error('No MFA session available');
    }

    const payload: AuthenticateMFASetupRequest = {
      mfa: this.session.mfa_code,
      code,
    };

    try {
      const response = await axios.post<AuthenticateMFASetupResponse>(
        `${this.config.host}/v2/console/authenticate/mfa/setup`,
        payload
      );

      // MFA is set so no need to require it anymore
      if (this.session) {
        this.session.mfa_code = null;
        localStorage.setItem(SESSION_LOCALSTORAGE_KEY, JSON.stringify(this.session));
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  private decodeJWT(token: string): any {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format');
      }

      const base64Raw = parts[1];
      const base64 = base64Raw.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window.atob(base64)
          .split('')
          .map((c) => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
          .join('')
      );

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Failed to decode JWT:', error);
      return null;
    }
  }

  public isAuthenticated(): boolean {
    if (!this.session?.token) return false;
    
    const claims = this.getClaims();
    if (!claims) return false;

    // Check if token is expired
    const now = Math.floor(Date.now() / 1000);
    return claims.exp > now;
  }
}

export const authService = new AuthenticationService();
export default authService;
